import { useCallback } from 'react';

import { useDrop } from 'react-dnd';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { REMINDER_CONSTRAINTS } from '@/components/atoms/ReminderTimeSelect/ReminderTimeSelect';
import { MODAL_REUSE_TIMER } from '@/components/organisms/Modals/ModalConductor';
import { cn } from '@/lib/utils';
import {
  buildReminderNode,
  collectChildrenForReparenting,
  TYPES,
  isFinalAction,
} from '@/redux/dialogs/helper';
import { selectDraftDialogNodes } from '@/redux/dialogs/selectors';
import { pushModal } from '@/redux/modals/actions';
import { addAction, addNode, moveAction, updateNode } from '@/redux/nodes/actions';
import { ReminderAction } from '@/shared/types/action';

import styles from './Placeholder.module.scss';

interface PlatceHolderProps {
  nodeId: string;
  requisiteIndex?: number;
  conditionIndex?: number;
  beforeActionId?: string;
  show?: boolean;
  label?: string;
  accept?: string;
  isLast?: boolean;
}

function Placeholder({
  accept = TYPES.ACTION,
  nodeId,
  show = false,
  label = '',
  beforeActionId = null,
  requisiteIndex = null,
  conditionIndex = null,
  isLast = false,
}: PlatceHolderProps) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const draftDialogNodes = useSelector(selectDraftDialogNodes);

  const dropActionInNode = useCallback(
    (item) => {
      if (item.move) {
        return dispatch(
          moveAction({
            source: item,
            target: { nodeId, beforeActionId, requisiteIndex, conditionIndex },
          })
        );
      }

      const newAction = {
        ...item.props,
        type: item.props?.type ?? item.subType,
        action_id: item.id,
      };
      const target = { nodeId, beforeActionId, requisiteIndex, conditionIndex };

      // Reminder action: if any sibling timer already lives under this parent, defer
      // the dispatch and let the user pick reuse vs. create via the modal.
      if (item.subType === 'reminder') {
        const reusableTimerNodes = draftDialogNodes.filter(
          (n) => n.parent_id === nodeId && n.is_reminder
        );

        if (reusableTimerNodes.length > 0) {
          dispatch(
            pushModal(MODAL_REUSE_TIMER, {
              candidates: reusableTimerNodes.map((n) => ({ nodeId: n.node_id })),
              pendingAction: newAction as ReminderAction,
              target,
            })
          );
          return;
        }

        const timerNode = buildReminderNode(nodeId, REMINDER_CONSTRAINTS.DEFAULT_SECONDS, t);
        newAction.trigger_node_id = timerNode.node_id;

        dispatch(addNode({ node: timerNode }));

        // Pull existing children of the parent under the new timer so it sits in the
        // flow path, not next to other downstream nodes.
        collectChildrenForReparenting(draftDialogNodes, nodeId).forEach((n) => {
          dispatch(updateNode({ nodeId: n.node_id, node: { parent_id: timerNode.node_id } }));
        });
      }

      dispatch(addAction({ ...target, newAction }));
    },
    [dispatch, nodeId, beforeActionId, requisiteIndex, conditionIndex, t, draftDialogNodes]
  );

  const [{ isOver, canDrop }, drop] = useDrop({
    accept,
    drop: dropActionInNode,
    canDrop: (item) => {
      if (item.subType) {
        const effectiveType = item.props?.type ?? item.subType;
        return !isFinalAction(effectiveType) || isLast;
      }
      return !isFinalAction(item.type);
    },
    collect: (monitor) => ({ isOver: monitor.isOver(), canDrop: monitor.canDrop() }),
  });

  return (
    <div
      ref={(node) => {
        drop(node);
      }}
      className={cn({
        [styles.placeholder]: true,
        [styles['placeholder--over']]: isOver,
        [styles['placeholder--can-drop']]: canDrop,
        [styles['placeholder--show']]: show,
      })}
    >
      {label && <p>{label}</p>}
    </div>
  );
}

export default Placeholder;
